// カテゴリ（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { C } from './core/model.js';
import { CONFIG, ICON, conf } from './config.js';
import { txt, txtHtml } from './text.js';
import { HANDS, MERGE, handName, isPair, layerSlot, mainKey, mergedOf, pairLabelKey, slotKey, slotKeys, syncLinks, tabLabel, tabShort } from './merge.js';
import { anim, catByKey, narrowUI, tallUI } from './store.js';
import { $, el, keyOf, sideDot } from './util.js';
import { findPart } from './core/data.js';
import { swapSlots } from './core/ops.js';
import { on } from './core/events.js';
import { setupGrad } from './colors.js';
import { buildColors } from './lr.js';
import { buildParts, dropGrid, pop } from './parts.js';
import { updateTools } from './tools.js';
import { setAdjust } from './side.js';
import { refreshLocks } from './random.js';
import { histCut, snapshot, toast } from './history.js';
import { scheduleAutosave } from './storage.js';
import type { Category } from './types.js';

// カテゴリの列のボタン（名前の span を持っておく）と列（自分でスクロールしたか。boot.ts）
export interface CatButton extends HTMLButtonElement { _label: HTMLSpanElement }
export interface CatStrip extends HTMLElement { _userScrolled?: boolean }
// 選んだあとのボタンの位置と大きさ（tabGeometry）
export interface TabGeo { x: number; y: number; w: number; h: number; row: boolean; scrollW: number }

// ---- カテゴリ ----
// カテゴリの絵は暗い帯に置く白い線なので、明るい列に置けるように色を入れかえる:
//   白（#FFFFFF）→ 文字の色（currentColor）、黒（帯の色で線を隠していたところ。持ちものの2つ）→ 列の背景の色（--icon-bg）
//   色の書いていない形（既定は黒）も、svg の fill（--icon-bg）で同じく背景の色になる
//   明るい列では線が細く見えるので、線の太さは 1.3 倍にする
// 色の付いた絵（茶色の線 #57383d で描いた組。DRESSUP2 など）: 後ろの白いふち（線の無い白い形）は今までどおり文字の色にし、
// 茶色の線で描いた形の白い塗り（くつした・手・はね など）は白のまま（2026-10-28。前は塗りまで文字の色＝黒くなっていた）
export function recolorIcon(node: Element) {
  [node].concat(Array.prototype.slice.call(node.querySelectorAll('*'))).forEach((n: Element) => {
    const s = n.getAttribute('style');
    const drawn = /#57383d/i.test((s || '') + (n.getAttribute('stroke') || ''));
    if (s) n.setAttribute('style', (drawn ? s : s.replace(/#FFFFFF/gi, 'currentColor').replace(/#000000/g, 'var(--icon-bg)'))
      .replace(/stroke-width:\s*([\d.]+)/g, (m, w: string) => 'stroke-width:' + Math.round(Number(w) * 130) / 100));
    if (drawn) return;
    ['fill', 'stroke'].forEach((a) => {
      const v = n.getAttribute(a);
      if (/^#(fff|ffffff)$/i.test(v || '')) n.setAttribute(a, 'currentColor');
      else if (/^#(000|000000)$/i.test(v || '')) n.setAttribute(a, 'var(--icon-bg)');
    });
  });
  return node;
}
// plain: 重ね付けでまとめたカテゴリの列のボタン用に、アイコンの「1」の丸を外す
export function categoryIcon(cat: Category, plain?: boolean) {
  const svg = el<SVGSVGElement>('svg', { viewBox: '0 0 ' + ICON + ' ' + ICON, 'aria-hidden': 'true' });
  const key = keyOf(cat);
  // メーカーの設定 catIcons の絵（番号入りなど）→ 左右でひと組の列のボタン（plain）は両方の絵（め など）→ カテゴリの絵（2026-10-28）
  const own = CONFIG.catIcons && CONFIG.catIcons[key];
  const both = plain ? pairLabelKey(key) : '';
  const src = (own && switchIcon(own)) || (both && switchIcon(both)) || switchIcon(key);
  if (src) {
    const icon = src.cloneNode(true) as Element;
    if (plain && !(own && switchIcon(own)) && !(both && switchIcon(both))) stripBadge(icon, switchIcon(MERGE[key]));
    svg.appendChild(recolorIcon(icon));
  }
  return svg;
}
export function switchIcon(id: string | undefined): Element | null | undefined { return C.switchRoot && id ? Array.prototype.filter.call(C.switchRoot.children, (g: Element) => g.id === id)[0] : null; }
// 1つ目と2つ目のアイコン（ぼうし・ぼうし 2 など）は、右下の白い丸の中の数字だけが違う。違う図形（数字）が1つだけなら、それと前の白い丸を外す
export function stripBadge(icon: Element, other: Element | null | undefined) {
  if (!other) return;
  const shapes = (g: Element): Element[] => Array.prototype.slice.call(g.querySelectorAll('path,circle,rect,polygon,ellipse'));
  const a = shapes(icon), b = shapes(other);
  if (a.length !== b.length) return;
  const diff = a.filter((n, i) => n.outerHTML !== b[i].outerHTML);
  if (diff.length !== 1) return;
  const prev = a[a.indexOf(diff[0]) - 1];
  diff[0].remove();
  if (prev && prev.tagName.toLowerCase() === 'circle') prev.remove();
}
// からだ（素体）はほとんど替えないので列の最後に（コラボ用の 9003〜9005 はほかのパーツと組み合わせにくい作り。ユーザー 2026-10-11）
export const CAT_LAST = conf('catLast', ['base']);
export const POSE_TIP = conf('poseTip', null); // 選んだら注意を出すカテゴリ（DRESSUP の素体 base。CHOCO は無し）
// 列に出さないカテゴリ（データには残す。KAWAII2 のアイシャドウ＝クラシック版のタブにも無く、レイヤーも隠れている。2026-10-28）
const HIDE_CATS = conf('hideCats', []);
export function buildCategories() {
  const box = $('cats');
  // 選んだ印（後ろを動く1枚。選んだカテゴリへすべって移る）
  const ind = document.createElement('span');
  ind.className = 'cat-ind';
  ind.id = 'catInd';
  ind.setAttribute('aria-hidden', 'true');
  box.appendChild(ind);
  Object.keys(MERGE).forEach((k) => { if (catByKey[k] && catByKey[MERGE[k]]) mergedOf[MERGE[k]] = k; });
  syncLinks(); // 左右でひと組ははじめ「両方」
  // 「キャラ」（プリセット）は列に出さず、セーブ・ロードの画面の「テンプレート」とロゴのメニュー「テンプレートから作る」から
  const list = C.cats.filter((cat) => cat.NAME !== 'chara' && CAT_LAST.indexOf(keyOf(cat)) < 0 && HIDE_CATS.indexOf(keyOf(cat)) < 0)
    .concat(CAT_LAST.map((k) => catByKey[k]).filter(Boolean));
  list.forEach((cat) => {
    const key = keyOf(cat);
    if (mergedOf[key]) return; // 2つ目の置き場所は、1つ目のボタンから
    // （整理用のタブの見出しは、下の buildGroups が列の前に足す）
    const btn = document.createElement('button') as CatButton;
    btn.type = 'button';
    btn.className = 'cat';
    btn.dataset.key = key;
    btn.title = tabLabel(key);
    btn.appendChild(categoryIcon(cat, !!slotKeys(key)));
    const label = document.createElement('span');
    label.className = 'cat-label';
    label.textContent = tabShort(key);
    btn.appendChild(label);
    btn._label = label;
    btn.addEventListener('click', () => {
      // 全身のメーカーのスマホ: 選んでいるカテゴリをもう一度押すと、パーツの一覧を引っこめる・出す（クラシック版と同じ。キャラの足元まで見える）。
      // 別のカテゴリを押したら出す
      if (tallUI) {
        const app = $('app');
        if (S.selKey === key) { app.classList.toggle('parts-hidden'); return; }
        app.classList.remove('parts-hidden');
      }
      selectCategory(key);
    });
    if (groupOfKey[key]) btn.dataset.group = groupOfKey[key];
    box.appendChild(btn);
  });
  buildGroups();
}
// ---- 整理用のタブ（PC だけ。2026-10-28〜、DRESSUP2。ユーザー「カテゴリが多いので、PC のときにカテゴリの左に整理用のタブ」） ----
// 設定 catGroups のタブを、カテゴリの列の左に縦に並べる。タブを押すとそのタブのカテゴリだけを列に出し、前にそのタブで選んでいたカテゴリ（無ければ先頭）を選ぶ。
// 元に戻すなどでほかのタブのカテゴリが選ばれたら、そのタブに切りかえる。スマホでは出さない（CSS。列は今までどおり全部）
const GROUPS = conf('catGroups', null) || [];
// タブの中の並びを catGroups に書いた順に（設定 groupOrder。既定はデータの順＝列の順のまま。KAWAII2 は目のタブの なみだ を最後に。2026-10-28）。
// PC のタブのときだけ CSS の order で並べかえる（スマホの列・データの順は変えない）
const GROUP_ORDER = conf('groupOrder', false);
// スマホ（タブを出さない横の帯）も catGroups に書いた順に並べる（設定 groupOrderSp。タブの順＝タブの中の書いた順。KAWAII2、2026-10-29）。
// 帯に出るカテゴリは全部 catGroups に書いておくこと（書いていないものは帯の最後に回る）
const GROUP_ORDER_SP = conf('groupOrderSp', false);
const groupOfKey: Record<string, string> = {};
GROUPS.forEach(([id, , keys]) => keys.forEach((k) => { groupOfKey[k] = id; }));
const lastInGroup: Record<string, string> = {};
function buildGroups() {
  if (!GROUPS.length) return;
  const nav = document.createElement('nav');
  nav.className = 'cat-groups';
  nav.id = 'catGroups';
  nav.setAttribute('aria-label', txt('categories'));
  GROUPS.forEach(([id, iconKey]) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'cat-group';
    b.dataset.group = id;
    const cat = catByKey[iconKey];
    if (cat) b.appendChild(categoryIcon(cat, true));
    const label = document.createElement('span');
    label.textContent = txt('catgroup.' + id);
    b.appendChild(label);
    b.addEventListener('click', () => { showGroup(id, true); });
    nav.appendChild(b);
  });
  const box = $('cats');
  box.parentNode!.insertBefore(nav, box);
  $('panel').classList.add('has-groups');
  if (GROUP_ORDER_SP) {
    $('panel').classList.add('group-order-sp');
    GROUPS.forEach(([, , keys], gi) => keys.forEach((k, i) => {
      const btn = box.querySelector<HTMLElement>('.cat[data-key="' + k + '"]');
      if (btn) btn.style.setProperty('--sp-order', String(gi * 100 + i));
    }));
  }
  if (GROUP_ORDER) {
    $('panel').classList.add('group-order');
    GROUPS.forEach(([, , keys]) => keys.forEach((k, i) => {
      const btn = box.querySelector<HTMLElement>('.cat[data-key="' + k + '"]');
      if (btn) btn.style.setProperty('--grp-order', String(i));
    }));
  }
}
// 並びの中の位置（一覧が入ってくる向きに使う）。groupOrder でタブを出しているとき（PC）は書いた順、ほかはデータの順
function catOrder(key: string): number {
  const nav = GROUP_ORDER ? document.getElementById('catGroups') : null;
  const m = mainKey(key), g = groupOfKey[m] && ((nav && getComputedStyle(nav).display !== 'none') || (GROUP_ORDER_SP && narrowUI.matches)) ? GROUPS.filter((x) => x[0] === groupOfKey[m])[0] : null; // スマホも書いた順なら（groupOrderSp）その順で
  if (g) return GROUPS.indexOf(g) * 1000 + g[2].indexOf(m);
  return C.cats.indexOf(catByKey[key]);
}
// タブの先頭のカテゴリ（groupOrder のときは書いた順の先頭、ほかは列の順の先頭）
function firstInGroup(box: HTMLElement, id: string): string | undefined {
  if (GROUP_ORDER) {
    const g = GROUPS.filter((x) => x[0] === id)[0];
    const k = g && g[2].filter((key) => box.querySelector('.cat[data-key="' + key + '"]'))[0];
    if (k) return k;
  }
  const first = box.querySelector<HTMLElement>('.cat[data-group="' + id + '"]');
  return first ? first.dataset.key : undefined;
}
function showGroup(id: string, pick: boolean) {
  const box = $('cats');
  if (box.dataset.group !== id) {
    box.dataset.group = id;
    box.querySelectorAll<HTMLElement>('.cat').forEach((b) => { b.classList.toggle('grp-off', !!b.dataset.group && b.dataset.group !== id); });
    document.querySelectorAll<HTMLElement>('#catGroups .cat-group').forEach((b) => {
      b.classList.toggle('on', b.dataset.group === id);
      b.setAttribute('aria-pressed', b.dataset.group === id ? 'true' : 'false');
    });
    box.scrollTop = 0;
  }
  if (!pick) return;
  const key = lastInGroup[id] && catByKey[lastInGroup[id]] ? lastInGroup[id] : firstInGroup(box, id);
  if (key) selectCategory(key);
}
// 選んだあとのボタンの位置と大きさ（帯の中の座標）。スマホの横の帯は選んだものだけ名前が開くので、
// 開き終わった並び（ほかの名前は全部閉じ、btn だけ開く）を、いまの幅から計算する。クラスを替える前に測ること
export const LABEL_PAD = 14; // 開いた名前の左右の余白（CSS の .cat.sel .cat-label の padding: 0 12px 0 2px）
export function tabGeometry(box: HTMLElement, btn: CatButton) {
  const geo = { x: btn.offsetLeft, y: btn.offsetTop, w: btn.offsetWidth, h: btn.offsetHeight, row: getComputedStyle(box).flexDirection === 'row' } as TabGeo;
  geo.scrollW = box.scrollWidth;
  if (!geo.row) return geo;
  let before = 0, all = 0;
  box.querySelectorAll<CatButton>('.cat').forEach((b) => {
    const w = b._label.getBoundingClientRect().width;
    all += w;
    if (b !== btn && b.offsetLeft < btn.offsetLeft) before += w;
  });
  const cs = getComputedStyle(btn._label);
  const own = btn._label.getBoundingClientRect().width;
  const full = btn._label.scrollWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) + LABEL_PAD;
  geo.x -= before;
  geo.w += full - own;
  geo.scrollW += full - all;
  return geo;
}
export function moveIndicator(geo: TabGeo, instant: boolean) {
  const ind = $('catInd');
  if (instant) ind.classList.add('instant');
  ind.style.transform = 'translate(' + geo.x + 'px,' + geo.y + 'px)';
  ind.style.width = geo.w + 'px';
  ind.style.height = geo.h + 'px';
  if (instant) { void ind.offsetWidth; ind.classList.remove('instant'); }
}
// 帯をスクロールして、選んだものを見えるところへ（1回で、なめらかに）。横の帯はまん中、縦の列は見えるところまで
export function scrollTab(box: HTMLElement, geo: TabGeo, instant: boolean) {
  const row = geo.row, size = row ? box.clientWidth : box.clientHeight;
  const pos = row ? geo.x : geo.y, len = row ? geo.w : geo.h, cur = row ? box.scrollLeft : box.scrollTop;
  let max = (row ? geo.scrollW : box.scrollHeight) - size, to;
  if (row) to = pos + len / 2 - size / 2;
  else if (pos < cur + 8) to = pos - 8;
  else if (pos + len > cur + size - 8) to = pos + len - size + 8;
  else to = cur; // もう見えている。前に押したものへのスクロールが続いていたら、ここで止める
  to = Math.max(0, Math.min(max, to));
  const opt: ScrollToOptions = { behavior: instant ? 'auto' : 'smooth' };
  opt[row ? 'left' : 'top'] = to;
  box.scrollTo(opt);
}
// 印を置き直す。smooth: 動かして合わせる（名前が開き終わったときの細かな直し）、scroll: 帯もスクロールし直す
export function placeIndicator(smooth: boolean, scroll: boolean) {
  const box = $('cats'), btn = box.querySelector<CatButton>('.cat.sel');
  if (!btn) return;
  const geo = tabGeometry(box, btn);
  moveIndicator(geo, !smooth || !anim);
  if (scroll) scrollTab(box, geo, !smooth || !anim);
}
export function selectCategory(key: string) {
  // まとめたカテゴリ: 2つ目のキーで呼ばれたら2つ目へ、1つ目のキー（列のボタン）なら今の置き場所へ。
  // 左右でひと組の右で呼ばれたとき（元に戻すで右が変わった・キャラの上で右を押した）は、「両方」ならそのまま、「左」なら「右」へ
  const pair = slotKeys(key);
  if (pair) {
    if (key === pair[1]) {
      if (!isPair(key)) layerSlot[pair[0]] = 1;
      else if (layerSlot[pair[0]] === 1) { layerSlot[pair[0]] = 2; syncLinks(); }
    }
    key = slotKey(pair);
  }
  const prevKey = S.selKey;
  S.selKey = key;
  const cat = catByKey[key];
  // 整理用のタブ: 選んだカテゴリのタブにする（印の位置を測る前に、列をそのタブのカテゴリに）
  const grp = groupOfKey[mainKey(key)];
  if (grp) { showGroup(grp, false); lastInGroup[grp] = mainKey(key); }
  const box = $<CatStrip>('cats'), btn = box.querySelector<CatButton>('.cat[data-key="' + mainKey(key) + '"]')!;
  const first = prevKey == null;
  if (prevKey !== key) S.lrSel = 0;
  // 全身のメーカーのスマホ: カテゴリを替えたら、右の列の「柄」「調整」から「色」に戻す（ユーザー依頼 2026-10-23。続いたままだと色を選ぼうとして戸惑う）
  if (tallUI && prevKey !== key) { S.colorTab = 'color'; setAdjust(false); }
  const geo = tabGeometry(box, btn);
  box._userScrolled = false;
  box.querySelectorAll('.cat').forEach((b) => {
    b.classList.toggle('sel', b === btn);
    b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
  });
  moveIndicator(geo, first || !anim);
  scrollTab(box, geo, first || !anim);
  $('catName').textContent = tabLabel(mainKey(key));
  buildLayerPick(key);
  setupGrad(cat);
  buildColors(cat);
  // 一覧が入ってくる向き（カテゴリの並びで後ろなら +、前なら -）
  const dir = first || prevKey === key ? 0 : (catOrder(key) > catOrder(prevKey) ? 1 : -1);
  buildParts(cat, dir);
  updateTools();
  refreshLocks();
  // 素体（ポーズ）のカテゴリを選んだら、ポーズを変えると袖・手袋が崩れることがある注意（DRESSUP。クラシック版の POSE_TIPS と同じ文言）
  if (POSE_TIP && key === POSE_TIP && prevKey !== key) toast(txt('pose_warning'), null, 6000);
  // 作る画面のキャラを動かす（emote.js の initStageEmote）が、目のまわりのカテゴリではまばたきを止める
  if (prevKey !== key) document.dispatchEvent(new CustomEvent('charatcreate:category', { detail: key }));
}

// 置き場所の切りかえ（「1つ目・2つ目」「左手・右手」。左右の色と同じ形、丸はパーツが入っているか）と、重ね付けの入れかえ
export function buildLayerPick(key: string) {
  const box = $('layerPick'), pair = slotKeys(key);
  box.textContent = '';
  box.hidden = !pair;
  if (!pair) return;
  if (isPair(key)) { buildPairPick(box, pair); return; }
  const hands = !!HANDS[pair[0]], cur = key === pair[1] ? 1 : 0;
  const seg = document.createElement('div');
  seg.className = 'lr';
  seg.setAttribute('role', 'radiogroup');
  seg.setAttribute('aria-label', txt(hands ? 'hands' : 'layers'));
  pair.forEach((k, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = i === cur ? 'on' : '';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', i === cur ? 'true' : 'false');
    const dot = document.createElement('i');
    dot.className = C.state[k] && C.state[k].parts != null && findPart(catByKey[k], C.state[k].parts) ? 'has' : '';
    const col = slotColor(k);
    if (col) dot.style.background = col; // 丸はその置き場所の色（2026-10-27〜。前はパーツがあるかだけ）
    b.appendChild(dot);
    b.appendChild(document.createTextNode(txt(hands ? handName(pair[0], i) : (i ? 'layer_2' : 'layer_1'))));
    b.addEventListener('click', () => { if (i !== cur) { layerSlot[pair[0]] = i; selectCategory(pair[0]); } });
    seg.appendChild(b);
  });
  box.appendChild(seg);
  if (hands) return; // 左手・右手は持つ手が決まっているので入れかえない
  const swap = document.createElement('button');
  swap.type = 'button';
  swap.className = 'layer-swap';
  swap.title = txt('layer_swap_title');
  swap.setAttribute('aria-label', swap.title);
  swap.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4v14M4 15l3 3 3-3M17 20V6M14 9l3-3 3 3"/></svg><span>' + txtHtml('layer_swap') + '</span>';
  swap.disabled = !C.state[pair[0]] && !C.state[pair[1]];
  swap.addEventListener('click', () => { swapLayers(pair); });
  box.appendChild(swap);
}
// 置き場所のいまの色（パーツが無ければ ''。色の無いカテゴリも ''）。グラデーションのカテゴリは上の色
function slotColor(k: string) {
  const st = C.state[k], cat = catByKey[k];
  if (!cat || !st || st.parts == null || !findPart(cat, st.parts)) return '';
  if (cat.GRAD.length > 1 && st.from) return st.from;
  return st.col || cat.COLOR[0] || cat.GRAD[0] || '';
}
// 左右でひと組の「両方・左・右」（左右の色と同じ形。丸はそれぞれの色、両方は左と右の半分ずつ）
function sideColor(k: string) { return slotColor(k) || (catByKey[k] && catByKey[k].COLOR[0]) || 'transparent'; }
// 色・パーツが変わったら、切りかえの丸をすぐ付け直す（2026-10-27 ユーザーの指摘。前はカテゴリを選び直すまで前の色のままだった）
let pickRaf = 0;
on('drawn', (cat) => {
  const pair = S.selKey ? slotKeys(S.selKey) : null;
  if (pickRaf || !pair || pair.indexOf(keyOf(cat)) < 0) return;
  pickRaf = requestAnimationFrame(() => { pickRaf = 0; if (S.selKey) buildLayerPick(S.selKey); });
});
function buildPairPick(box: HTMLElement, pair: [string, string]) {
  const cur = layerSlot[pair[0]] || 0;
  const seg = document.createElement('div');
  seg.className = 'lr';
  seg.setAttribute('role', 'radiogroup');
  seg.setAttribute('aria-label', txt('side_lr'));
  ['lr_both', 'lr_left', 'lr_right'].forEach((label, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = i === cur ? 'on' : '';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', i === cur ? 'true' : 'false');
    const dot = document.createElement('i');
    dot.className = 'has';
    dot.style.background = sideDot(i, sideColor(pair[0]), sideColor(pair[1]));
    b.appendChild(dot);
    b.appendChild(document.createTextNode(txt(label)));
    b.addEventListener('click', () => { if (i !== cur) setPairSlot(pair, i); });
    seg.appendChild(b);
  });
  box.appendChild(seg);
}
// 「両方・左・右」を変える（スマホの右の列の「左右」のボタンも）
export function setPairSlot(pair: [string, string], i: number) {
  layerSlot[pair[0]] = i;
  // 一覧の絵を側ごとに描くメーカー（設定 pairThumbSide）は、覚えている一覧を捨てて描き直す（「両方」と「左」は同じカテゴリの一覧）
  if (CONFIG.pairThumbSide) { dropGrid(pair[0]); dropGrid(pair[1]); }
  syncLinks();
  selectCategory(pair[0]);
}
// 1つ目と2つ目の中身（パーツ・色・柄・位置など、保存データの1行ぶん）を入れかえる。重なりの上下が入れかわる
export function swapLayers(pair: [string, string]) {
  histCut();
  const snap = snapshot();
  swapSlots(pair[0], pair[1]);
  scheduleAutosave();
  selectCategory(S.selKey);
  pop();
  toast(txt('layer_swapped'), snap);
}
